import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNotes } from '../hooks/useNotes';
import ReactMarkdown from 'react-markdown';
import { formatDate } from '../lib/formatDate';

const Notes: React.FC = () => {
  const { notes, isLoading: loading, error, updateNotes, isUpdating } = useNotes();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editError, setEditError] = useState('');

  const handleEditClick = () => {
    if (notes) {
      setEditContent(notes.content);
      setIsEditing(true);
      setEditError('');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent('');
    setEditError('');
  };

  const handleSave = async () => {
    try {
      setEditError('');
      updateNotes({ content: editContent });
      setIsEditing(false);
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Failed to update notes');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{(error as any)?.message || 'Failed to load notes'}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            My Notes
          </Typography>
          {notes && (
            <Typography variant="body2" color="text.secondary">
              Last updated: {formatDate(notes.updatedAt)}
            </Typography>
          )}
        </Box>
        
        {!isEditing && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            disabled={isUpdating}
          >
            Edit Notes
          </Button>
        )}
      </Box>

      <Paper elevation={2}>
        {isEditing ? (
          <Box p={3}>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editError}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              rows={20}
              label="Notes"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Enter your notes, reminders, to-do items, etc. (Markdown supported)..."
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button onClick={handleCancel} disabled={isUpdating}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save Notes'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            p: 3,
            minHeight: '400px',
            '& h1': { 
              fontSize: '2rem', 
              fontWeight: 'bold', 
              mb: 2, 
              mt: 0,
              color: 'primary.main'
            },
            '& h2': { 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              mb: 1.5, 
              mt: 3,
              color: 'text.primary'
            },
            '& h3': { 
              fontSize: '1.25rem', 
              fontWeight: 'bold', 
              mb: 1, 
              mt: 2,
              color: 'text.primary'
            },
            '& p': { 
              mb: 1.5, 
              lineHeight: 1.6 
            },
            '& ul, & ol': { 
              mb: 1.5, 
              pl: 3 
            },
            '& li': { 
              mb: 0.5, 
              lineHeight: 1.6 
            },
            '& strong': { 
              fontWeight: 'bold' 
            },
            '& em': { 
              fontStyle: 'italic' 
            },
            '& blockquote': { 
              borderLeft: '4px solid #1976d2', 
              pl: 2, 
              ml: 0, 
              my: 2,
              fontStyle: 'italic',
              color: 'text.secondary'
            },
            '& hr': { 
              my: 3, 
              borderColor: 'divider' 
            }
          }}>
            {notes && notes.content ? (
              <ReactMarkdown>{notes.content}</ReactMarkdown>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No notes yet. Click "Edit Notes" to add your daily notes, reminders, or to-do items.
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Notes;

